import React, { useMemo } from "react";
import { TransactionMetricCard } from "./TransactionMetricCard";
import { TransactionAnalytics } from "../types";
import { formatCurrencyAmount } from "../utils/formatCurrency";

interface TransactionMetricsSectionProps {
  loading: boolean;
  analytics?: TransactionAnalytics;
  totalCount?: number;
  currency?: string;
}

export const TransactionMetricsSection: React.FC<TransactionMetricsSectionProps> = ({
  loading,
  analytics,
  totalCount,
  currency = "NGN",
}) => {
  // 1. Transaction Volume & Inflow/Outflow calculation
  const totalInflow = Number(
    analytics?.transactionVolume?.inflow ??
      analytics?.totalInflow ??
      analytics?.inflowVolume ??
      0
  );
  const totalOutflow = Number(
    analytics?.transactionVolume?.outflow ??
      analytics?.totalOutflow ??
      analytics?.outflowVolume ??
      0
  );
  const computedSum = totalInflow + totalOutflow;
  const rawTotalVolume =
    analytics?.transactionVolume?.total !== undefined
      ? Number(analytics.transactionVolume.total)
      : analytics?.totalVolume !== undefined
        ? Number(analytics.totalVolume)
        : computedSum > 0
          ? computedSum
          : undefined;

  const volumeDisplay = loading
    ? "--"
    : formatCurrencyAmount(rawTotalVolume, currency);

  // Inflow vs Outflow percentages for Donut
  const { inflowPercent, outflowPercent } = useMemo(() => {
    if (computedSum > 0) {
      const inPct = Math.round((totalInflow / computedSum) * 100);
      const outPct = Math.max(0, 100 - inPct);
      return { inflowPercent: inPct, outflowPercent: outPct };
    }
    return { inflowPercent: 0, outflowPercent: 0 };
  }, [totalInflow, computedSum]);

  const volumeDonutSegments = useMemo(() => {
    if (computedSum <= 0) return undefined;
    return [
      { value: inflowPercent, color: "#6F00FF" },
      { value: outflowPercent, color: "#CBB0FF" },
    ];
  }, [computedSum, inflowPercent, outflowPercent]);

  // 2. Revenue from fees
  const rawFeeRevenue =
    analytics?.revenueFromFees?.total ??
    analytics?.feeRevenue;

  const rawAvgFee =
    analytics?.revenueFromFees?.averagePerTransaction ??
    analytics?.avgFee;

  const feeRevenueDisplay = loading
    ? "--"
    : formatCurrencyAmount(rawFeeRevenue, currency);

  const avgFeeDisplay =
    rawAvgFee !== undefined && rawAvgFee !== null
      ? `${formatCurrencyAmount(rawAvgFee, currency)} avg. fee / transaction`
      : undefined;

  // 3. Transactions Count & Status breakdown
  const countsObj = analytics?.transactionsCount;
  const resolvedCount =
    countsObj?.total ??
    totalCount ??
    analytics?.totalCount;

  const countDisplay = loading
    ? "--"
    : resolvedCount !== undefined && resolvedCount !== null
      ? resolvedCount >= 1000
        ? `${(resolvedCount / 1000).toFixed(1)}k`
        : String(resolvedCount)
      : "0";

  const countDonutSegments = useMemo(() => {
    const counts = analytics?.transactionsCount;
    const flagged = Number(
      counts?.flagged ??
      analytics?.flaggedCount ??
      0
    );
    const processing = Number(
      counts?.processing ??
      analytics?.processingCount ??
      0
    );
    const completed = Number(
      counts?.completed ??
      counts?.success ??
      analytics?.successCount ??
      0
    );
    const failed = Number(
      counts?.failed ??
      analytics?.failedCount ??
      0
    );

    const total = flagged + processing + completed + failed;
    if (total > 0) {
      return [
        { value: Math.round((flagged / total) * 100), color: "#FEC84B" },
        { value: Math.round((processing / total) * 100), color: "#9E77ED" },
        { value: Math.round((completed / total) * 100), color: "#73E2A7" },
        { value: Math.round((failed / total) * 100), color: "#FDA29B" },
      ];
    }
    return undefined;
  }, [
    analytics?.transactionsCount,
    analytics?.successCount,
    analytics?.processingCount,
    analytics?.failedCount,
    analytics?.flaggedCount,
  ]);

  return (
    <section
      className="my-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      aria-label="Transaction Analytics Overview"
    >
      {/* Card 1: Transaction Volume */}
      <TransactionMetricCard
        label="Transaction Volume"
        value={volumeDisplay}
        donutSegments={volumeDonutSegments}
        legend={
          volumeDonutSegments
            ? [
                { label: "Inflow", color: "#6F00FF" },
                { label: "Outflow", color: "#CBB0FF" },
              ]
            : undefined
        }
      />

      {/* Card 2: Revenue from fees */}
      <TransactionMetricCard
        label="Revenue from fees"
        value={feeRevenueDisplay}
        subtext={avgFeeDisplay}
      />

      {/* Card 3: Transactions count */}
      <TransactionMetricCard
        label="Transactions count"
        value={countDisplay}
        donutSegments={countDonutSegments}
        className="sm:col-span-2 lg:col-span-1"
        legend={
          countDonutSegments
            ? [
                { label: "Flagged", color: "#FEC84B" },
                { label: "Processing", color: "#9E77ED" },
                { label: "Completed", color: "#73E2A7" },
                { label: "Failed", color: "#FDA29B" },
              ]
            : undefined
        }
      />
    </section>
  );
};

